import { lazy, Suspense } from 'react';

import { CardSkeleton, ListSkeleton } from '@/shared/components/skeletons';
import { Skeleton } from '@/shared/ui/skeleton';

/*
 * The heavy parts of the module (card, group tab) load on demand; these wrappers are what the registry renders.
 */

const GoalPanel = lazy(() => import('./components/panel/GoalPanel'));
const GroupMapsTab = lazy(() => import('./components/GroupMapsTab'));
const ProfileGoalsTab = lazy(() => import('./components/ProfileGoalsTab'));
const MapsPage = lazy(() => import('./pages/MapsPage'));

function PanelFallback() {
  return (
    <div className="space-y-4 p-6" aria-busy>
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="h-4 w-40" />
      <ListSkeleton rows={4} avatar={false} />
    </div>
  );
}

export function LazyGoalPanel({ id }: { id: string }) {
  return (
    <Suspense fallback={<PanelFallback />}>
      <GoalPanel key={id} id={id} />
    </Suspense>
  );
}

export function LazyGroupMapsTab({ groupId }: { groupId: string }) {
  return (
    <Suspense fallback={<ListSkeleton rows={3} />}>
      <GroupMapsTab groupId={groupId} />
    </Suspense>
  );
}

export function LazyProfileGoalsTab({ userId }: { userId: string }) {
  return (
    <Suspense fallback={<ListSkeleton rows={3} />}>
      <ProfileGoalsTab userId={userId} />
    </Suspense>
  );
}

/** The maps list inside the goals tab of the Tasks page (`pageSections` of `tasks`). */
export function LazyGoalsSection() {
  return (
    <Suspense
      fallback={
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy>
          {Array.from({ length: 3 }, (_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      }
    >
      <MapsPage embedded />
    </Suspense>
  );
}
